import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import { HelpText, Input, cn } from '../ui';

// ─── SkillsInput — the skill chips on a website team profile ─────────────
//
// Typing a skill and pressing Enter, Tab or comma commits it; Backspace in an
// empty box takes the last one back. A pasted "React, Node, AWS" splits into
// three, because that is how a CV lists them and retyping it is work the admin
// should not have to do.
//
// Chips rather than one comma-separated string: the website renders them as
// separate pills, so showing them separately here is the only way the form
// agrees with the page. The comma-splitting server side
// (vc_be/utils/cmsTeamProfile.normaliseSkills) is the same rule, so neither
// side can accept something the other would not.
//
// Props:
//   value     — string[]
//   onChange  — (string[])
//   max       — the cap, mirrored from the server
//   maxLength — per-skill cap, mirrored from the server
//   id        — from FormField, so the label points at the text box

const SkillsInput = ({ value = [], onChange, max = 20, maxLength = 40, id, ...rest }) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const skills = Array.isArray(value) ? value : [];
  const full = skills.length >= max;

  const add = (raw) => {
    const incoming = String(raw)
      .split(',')
      .map((s) => s.trim().slice(0, maxLength))
      .filter(Boolean);
    if (!incoming.length) return;
    const next = [...skills];
    for (const skill of incoming) {
      if (next.length >= max) break;
      if (next.some((s) => s.toLowerCase() === skill.toLowerCase())) continue;
      next.push(skill);
    }
    if (next.length !== skills.length) onChange(next);
    setDraft('');
  };

  const removeAt = (index) => {
    onChange(skills.filter((_, i) => i !== index));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || (e.key === 'Tab' && draft.trim())) {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && skills.length) {
      e.preventDefault();
      setDraft(skills[skills.length - 1]);
      onChange(skills.slice(0, -1));
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {skills.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0">
          {skills.map((skill, index) => (
            <li
              key={`${skill}-${index}`}
              className={cn(
                'tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid',
                'tw-border-primary/25 tw-bg-primary/10 tw-py-1 tw-pl-2.5 tw-pr-1 tw-text-xs tw-text-primary',
              )}
            >
              {skill}
              <button
                type="button"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${skill}`}
                className={cn(
                  'tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full',
                  'tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary tw-focus-ring',
                  'hover:tw-bg-primary/20',
                )}
              >
                <X size={11} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Input
        {...rest}
        id={id}
        ref={inputRef}
        size="lg"
        value={draft}
        disabled={full}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        // Leaving the field commits what was typed: a half-typed skill the
        // admin walked away from and then saved would otherwise be lost
        // without a word.
        onBlur={() => add(draft)}
        placeholder={full ? `${max} skills is the maximum` : 'Type a skill and press Enter'}
      />
      <HelpText>
        {full
          ? `You have added the maximum of ${max}. Remove one to add another.`
          : `Press Enter or comma after each. ${skills.length} of ${max} added.`}
      </HelpText>
    </div>
  );
};

export default SkillsInput;
